import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { computeEstimatesList } from "../../api/quotations/index.ts";
import {
  formatCurrency,
  currencyFormatterMXN,
} from "../../services/valueFormatters.js";

interface QuotingPlotProps {
  props: any;
}

interface CostData {
  packingCost: number[];
  rawMaterial: number[];
  startingCost: number[];
  operatingCost: number[];
  utility: number[];
  sellerCommission: number[];
  totalCost: number[];
}

const QuotingPlot: React.FC<QuotingPlotProps> = ({ props }) => {
  const { id } = props;
  const [loading, setLoading] = useState(true);
  const [costData, setCostData] = useState<CostData>({
    packingCost: [],
    rawMaterial: [],
    startingCost: [],
    operatingCost: [],
    utility: [],
    sellerCommission: [],
    totalCost: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const quantities = [500, 1000, 5000, 10000, 20000, 50000, 100000];
        const response = await computeEstimatesList(id, quantities);
        const result = response.data;

        // Extract cost components and calculate unit costs
        const packingCost = result.map(
          (item) => item.packingCost / quantities[result.indexOf(item)]
        );
        const rawMaterial = result.map(
          (item) => item.rawMaterialCost / quantities[result.indexOf(item)]
        );
        const startingCost = result.map(
          (item) => item.startingCost / quantities[result.indexOf(item)]
        );
        const operatingCost = result.map(
          (item) => item.operatingCost / quantities[result.indexOf(item)]
        );
        const utility = result.map(
          (item) => item.utility / quantities[result.indexOf(item)]
        );
        const sellerCommission = result.map(
          (item) => item.sellerCommission / quantities[result.indexOf(item)]
        );
        const totalCost = result.map(
          (item) => item.totalCost / quantities[result.indexOf(item)]
        );
        setCostData({
          packingCost,
          rawMaterial,
          startingCost,
          operatingCost,
          utility,
          sellerCommission,
          totalCost,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const xLabels = [
    "500",
    "1,000",
    "5,000",
    "10,000",
    "20,000",
    "50,000",
    "100,000",
  ];

  return (
    <Box p={2} sx={{ width: "100%", height: 400 }}>
      <LineChart
        xAxis={[
          {
            scaleType: "point",
            data: xLabels,
            label: "Cantidad",
          },
        ]}
        yAxis={[
          {
            ...currencyFormatterMXN,
            label: "Costo Unitario",
          },
        ]}
        series={[
          {
            ...currencyFormatterMXN,
            data: costData.totalCost,
            label: "Total",
            area: true,
            showMark: true,
          },
          {
            ...currencyFormatterMXN,
            data: costData.startingCost,
            label: "Costo de Arranque",
            area: true,
            stack: "total",
            showMark: true,
          },
          {
            ...currencyFormatterMXN,
            data: costData.rawMaterial,
            label: "Materia Prima",
            area: true,
            stack: "total",
            showMark: true,
          },
          {
            ...currencyFormatterMXN,
            data: costData.operatingCost,
            label: "Costo Operativo",
            area: true,
            stack: "total",
            showMark: true,
          },
          {
            ...currencyFormatterMXN,
            data: costData.packingCost,
            label: "Costo de Empaque",
            area: true,
            stack: "total",
            showMark: true,
          },
          {
            ...currencyFormatterMXN,
            data: costData.utility,
            label: "Utilidad",
            area: true,
            stack: "total",
            showMark: true,
          },
          {
            ...currencyFormatterMXN,
            data: costData.sellerCommission,
            label: "Comisión Vendedor",
            area: true,
            stack: "total",
            showMark: true,
          },
        ]}
        margin={{ top: 20, right: 20, bottom: 50, left: 80 }}
      />
    </Box>
  );
};

export default QuotingPlot;
